import { useCallback, useEffect, useRef, useState } from 'react'

import {
  REPORT_PROGRESS_STAGES,
  getReportProgressMessage,
  resolveWaitStage,
} from './reportProgressStages'

const TICK_MS = 500

/**
 * Manages report-generation progress UI state for a long-running request.
 *
 * - Call `start()` when generation begins (optionally with an initial stage).
 * - Call `setStage(stage)` when a real client/server phase is known.
 * - Call `setPercent(n)` only when true completion percent is known.
 * - Call `complete()` or `fail()` / `cancel()` when finished.
 *
 * While active without an explicit stage, status messages advance by elapsed
 * time (indeterminate). Percentages are never invented from elapsed time.
 */
export default function useReportProgress({ active = false } = {}) {
  const [isActive, setIsActive] = useState(Boolean(active))
  const [stage, setStageState] = useState(REPORT_PROGRESS_STAGES.PREPARING)
  const [message, setMessage] = useState(
    getReportProgressMessage(REPORT_PROGRESS_STAGES.PREPARING)
  )
  const [percent, setPercentState] = useState(null)
  const [wasCancelled, setWasCancelled] = useState(false)
  const [error, setError] = useState(null)

  const explicitStageRef = useRef(false)
  const startedAtRef = useRef(null)
  const abortControllerRef = useRef(null)
  const tickRef = useRef(null)

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
  }, [])

  const applyStage = useCallback((nextStage, nextMessage) => {
    setStageState(nextStage)
    setMessage(nextMessage || getReportProgressMessage(nextStage))
  }, [])

  const startWaitTicker = useCallback(() => {
    clearTick()
    tickRef.current = setInterval(() => {
      if (explicitStageRef.current || !startedAtRef.current) return
      const elapsed = Date.now() - startedAtRef.current
      applyStage(resolveWaitStage(elapsed))
    }, TICK_MS)
  }, [applyStage, clearTick])

  const start = useCallback(
    ({
      stage: initialStage,
      message: initialMessage,
      percent: initialPercent,
    } = {}) => {
      explicitStageRef.current = Boolean(initialStage)
      startedAtRef.current = Date.now()
      setWasCancelled(false)
      setError(null)
      setPercentState(
        initialPercent === undefined || initialPercent === null
          ? null
          : initialPercent
      )
      applyStage(
        initialStage || REPORT_PROGRESS_STAGES.PREPARING,
        initialMessage
      )
      setIsActive(true)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (!initialStage) {
        startWaitTicker()
      } else {
        clearTick()
      }

      return abortControllerRef.current
    },
    [applyStage, clearTick, startWaitTicker]
  )

  const setStage = useCallback(
    (nextStage, nextMessage) => {
      if (!nextStage) return
      explicitStageRef.current = true
      clearTick()
      applyStage(nextStage, nextMessage)
    },
    [applyStage, clearTick]
  )

  const setPercent = useCallback((value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      setPercentState(null)
      return
    }
    setPercentState(value)
  }, [])

  const complete = useCallback(() => {
    clearTick()
    explicitStageRef.current = false
    startedAtRef.current = null
    abortControllerRef.current = null
    setIsActive(false)
    setPercentState(null)
    setError(null)
  }, [clearTick])

  const fail = useCallback(
    (err) => {
      clearTick()
      explicitStageRef.current = false
      startedAtRef.current = null
      abortControllerRef.current = null
      setError(err || new Error('Report generation failed'))
      setIsActive(false)
      setPercentState(null)
    },
    [clearTick]
  )

  const cancel = useCallback(() => {
    clearTick()
    explicitStageRef.current = false
    startedAtRef.current = null
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setWasCancelled(true)
    setError(null)
    setIsActive(false)
    setPercentState(null)
  }, [clearTick])

  const getSignal = useCallback(() => abortControllerRef.current?.signal, [])

  useEffect(() => {
    if (active && !isActive) {
      start()
    } else if (!active && isActive) {
      complete()
    }
    // Intentionally sync only when parent `active` prop flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  useEffect(() => {
    return () => {
      clearTick()
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    }
  }, [clearTick])

  return {
    isActive,
    stage,
    message,
    percent,
    wasCancelled,
    error,
    start,
    setStage,
    setPercent,
    complete,
    fail,
    cancel,
    getSignal,
  }
}
